import {
  Box,
  Flex,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import Head from 'next/head';
import Image from 'next/image';
import PropTypes from 'prop-types';

const PRODUCT_NAME = 'Appraisers Now';
const TAGLINE = 'Welcome to the Cloud';
const LOGO_SRC = 'https://cdn.anow.com/branding/anow-logo-white.png';

const DEFAULT_HIGHLIGHTS = [
  'Manage orders and assignments from anywhere',
  'Keep your profile and credentials in one place',
  'Built for appraisers who work in the cloud',
];

const DEFAULT_BRAND_HEADING = (
  <>
    Built to
    {' '}
    <Text as="span" color="#35c3bd">
      capture,
    </Text>
    {' '}
    manage, and grow your appraisal business.
  </>
);

const DEFAULT_BRAND_DESCRIPTION = 'Automate order intake, streamline your workflow, and unlock financial'
  + ' visibility so you can focus on appraising, not admin.';

function AuthCtaButton({ href, children }) {
  return (
    <Link
      as="a"
      href={href}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={{ base: 'full', sm: 'auto' }}
      minW={{ sm: '200px' }}
      px={8}
      py={3.5}
      color="green.900"
      bgColor="brand.green"
      fontSize="sm"
      fontFamily="Poppins, sans-serif"
      fontWeight="700"
      letterSpacing="0.04em"
      textDecoration="none"
      textTransform="uppercase"
      borderRadius="md"
      boxShadow="0 10px 24px rgba(104, 162, 37, 0.28)"
      transition="all 0.2s ease"
      _hover={{
        backgroundColor: 'green.600',
        textDecoration: 'none',
        transform: 'translateY(-1px)',
        boxShadow: '0 12px 28px rgba(104, 162, 37, 0.4)',
      }}
      _active={{
        transform: 'translateY(0)',
      }}
    >
      {children}
    </Link>
  );
}

AuthCtaButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function BrandPanel({
  eyebrow = TAGLINE,
  heading = DEFAULT_BRAND_HEADING,
  description = DEFAULT_BRAND_DESCRIPTION,
  highlightsLabel = null,
  highlights = DEFAULT_HIGHLIGHTS,
}) {
  return (
    <Flex
      display={{ base: 'none', lg: 'flex' }}
      flex={1}
      direction="column"
      justify="space-between"
      px={{ lg: 12, xl: 16 }}
      py={12}
      position="relative"
      overflow="hidden"
      bg="#03284a"
      bgImage="radial-gradient(circle at 8% 45%, rgba(28, 168, 184, 0.34), transparent 34%), radial-gradient(circle at 92% 62%, rgba(57, 188, 151, 0.24), transparent 32%), linear-gradient(115deg, #032d50 0%, #032449 52%, #042e4d 100%)"
    >
      <Box
        position="absolute"
        top="-20%"
        right="-10%"
        w="420px"
        h="420px"
        borderRadius="full"
        bg="brand.blue"
        opacity={0.16}
        filter="blur(80px)"
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-5%"
        w="320px"
        h="320px"
        borderRadius="full"
        bg="brand.green"
        opacity={0.18}
        filter="blur(60px)"
      />

      <Box position="relative" zIndex={1}>
        <Image src={LOGO_SRC} alt="Anow Logo" width={100} height={100} />
      </Box>

      <VStack align="flex-start" spacing={5} position="relative" zIndex={1} maxW="520px">
        {eyebrow && (
          <Flex
            align="center"
            gap={2}
            px={3}
            py={1.5}
            border="1px solid rgba(118, 205, 214, 0.34)"
            borderRadius="full"
            bg="rgba(3, 41, 72, 0.62)"
          >
            <Box w="7px" h="7px" borderRadius="full" bg="brand.green" />
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="blue.100"
            >
              {eyebrow}
            </Text>
          </Flex>
        )}
        <Heading
          as="p"
          fontSize={{ lg: '4xl', xl: '5xl' }}
          lineHeight="1.08"
          letterSpacing="-0.035em"
          fontFamily="Poppins, sans-serif"
          fontWeight="700"
          color="white"
        >
          {heading}
        </Heading>
        <Text fontSize="md" color="blue.100" lineHeight="1.75">
          {description}
        </Text>
        <Stack spacing={3} pt={2}>
          {highlightsLabel && (
            <Text fontSize="sm" fontWeight="600" color="white">
              {highlightsLabel}
            </Text>
          )}
          {highlights.map((item) => (
            <Flex key={item} align="flex-start" gap={3}>
              <Text color="green.300" fontSize="sm">✓</Text>
              <Text fontSize="sm" color="blue.100" lineHeight="1.6">
                {item}
              </Text>
            </Flex>
          ))}
        </Stack>
      </VStack>

      <Text position="relative" zIndex={1} fontSize="xs" color="blue.200">
        ©
        {' '}
        {new Date().getFullYear()}
        {' '}
        {PRODUCT_NAME}
      </Text>
    </Flex>
  );
}

BrandPanel.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.node,
  description: PropTypes.node,
  highlightsLabel: PropTypes.string,
  highlights: PropTypes.arrayOf(PropTypes.string),
};

export default function AuthLanding({
  title,
  subtitle,
  buttonText,
  buttonHref,
  alternatePrompt,
  alternateHref,
  alternateLinkText,
  brandEyebrow = TAGLINE,
  brandHeading = DEFAULT_BRAND_HEADING,
  brandDescription = DEFAULT_BRAND_DESCRIPTION,
  brandHighlightsLabel = null,
  brandHighlights = DEFAULT_HIGHLIGHTS,
  children = null,
}) {
  return (
    <>
      <Head>
        <title>
          {title}
          {' '}
          ·
          {' '}
          {PRODUCT_NAME}
        </title>
      </Head>
      <Flex minH="100vh" bg="gray.100">
        <BrandPanel
          eyebrow={brandEyebrow}
          heading={brandHeading}
          description={brandDescription}
          highlightsLabel={brandHighlightsLabel}
          highlights={brandHighlights}
        />

        <Flex
          flex={{ base: 1, lg: '0 0 520px', xl: '0 0 560px' }}
          direction="column"
          align="center"
          justify="center"
          px={{ base: 6, sm: 10 }}
          py={{ base: 10, lg: 12 }}
          bg="white"
          position="relative"
        >
          <Box
            display={{ base: 'block', lg: 'none' }}
            w="full"
            maxW="420px"
            mb={8}
            px={5}
            py={4}
            borderRadius="2xl"
            bgGradient="linear(135deg, green.900, blue.900)"
          >
            {brandEyebrow && (
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.18em"
                textTransform="uppercase"
                color="green.300"
                mb={1}
              >
                {brandEyebrow}
              </Text>
            )}
            <Text
              fontFamily="Poppins, sans-serif"
              fontWeight="700"
              fontSize="xl"
              color="white"
            >
              {PRODUCT_NAME}
            </Text>
          </Box>

          <Box w="full" maxW="420px">
            <Heading
              as="h1"
              fontSize={{ base: '3xl', sm: '4xl' }}
              fontFamily="Poppins, sans-serif"
              fontWeight="700"
              lineHeight="1.15"
              color="gray.900"
              mb={4}
            >
              {title}
            </Heading>
            <Box fontSize="md" color="gray.600" lineHeight="1.7" mb={8}>
              {subtitle}
            </Box>

            {children || (
              <AuthCtaButton href={buttonHref}>
                {buttonText}
              </AuthCtaButton>
            )}

            {alternatePrompt && (
              <Text fontSize="sm" color="gray.600" pt={8}>
                {alternatePrompt}
                {' '}
                <Link
                  href={alternateHref}
                  fontWeight="600"
                  color="green.700"
                  _hover={{ color: 'green.800', textDecoration: 'none' }}
                >
                  {alternateLinkText}
                </Link>
              </Text>
            )}
          </Box>
        </Flex>
      </Flex>
    </>
  );
}

AuthLanding.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node.isRequired,
  buttonText: PropTypes.string,
  buttonHref: PropTypes.string,
  alternatePrompt: PropTypes.string,
  alternateHref: PropTypes.string,
  alternateLinkText: PropTypes.string,
  brandEyebrow: PropTypes.string,
  brandHeading: PropTypes.node,
  brandDescription: PropTypes.node,
  brandHighlightsLabel: PropTypes.string,
  brandHighlights: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

AuthLanding.defaultProps = {
  buttonText: null,
  buttonHref: null,
  alternatePrompt: null,
  alternateHref: null,
  alternateLinkText: null,
  children: null,
};
